import { Link } from "react-router";
import type { Game } from "../types/Game";
import { ARCHETYPE_LABELS } from "../types/Game";
import { RatingStars } from "./RatingStars";

interface GameCardProps {
	game: Game;
	/**
	 * "default" — full card with thumbnail, title, meta row.
	 * "compact" — tighter variant for related game rows.
	 */
	variant?: "default" | "compact";
}

/**
 * Formats large play counts into 1.2K / 3.4M style strings. Portal-local
 * utility — the shared NumberFormatter in @genieforge/game-common requires a
 * Phaser runtime peer dep which the React portal does not have.
 */
function formatPlays(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
	return String(n);
}

/**
 * Premium game card with neon hover glow, star rating display, category badge,
 * and trending/featured badges. The most-used UI element in the portal —
 * appears on Home, Browse, Search, and Game Detail pages.
 */
export function GameCard({ game, variant = "default" }: GameCardProps) {
	const isCompact = variant === "compact";
	return (
		<Link
			to={`/game/${game.slug}`}
			className="group flex flex-col rounded-[var(--radius-card)] bg-surface border border-border overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-[0_0_30px_-8px_var(--color-primary-glow)] hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-primary"
		>
			{/* Thumbnail — solid color + emoji glyph until real screenshots ship */}
			<div
				className="relative w-full aspect-[16/10] flex items-center justify-center overflow-hidden"
				style={{ backgroundColor: game.thumbnail.bg }}
			>
				<span
					className="text-6xl md:text-7xl transition-transform duration-500 group-hover:scale-110"
					style={{
						filter: `drop-shadow(0 0 28px ${game.thumbnail.fg}66)`,
					}}
					aria-hidden="true"
				>
					{game.thumbnail.emoji}
				</span>

				{/* Gradient vignette for contrast */}
				<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />

				{/* Play button overlay on hover */}
				<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/30 backdrop-blur-[1px]">
					<div className="h-14 w-14 rounded-full bg-primary text-background flex items-center justify-center shadow-[0_0_40px_rgba(0,255,136,0.5)] scale-90 group-hover:scale-100 transition-transform duration-300">
						<svg
							viewBox="0 0 24 24"
							fill="currentColor"
							className="h-6 w-6 ml-0.5"
							aria-hidden="true"
						>
							<path d="M8 5v14l11-7z" />
						</svg>
					</div>
				</div>

				{/* Category badge — top left */}
				<span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/60 text-white/90 backdrop-blur-sm border border-white/10">
					{ARCHETYPE_LABELS[game.archetype]}
				</span>

				{/* Trending / Featured badges — top right */}
				<div className="absolute top-2 right-2 flex items-center gap-1.5">
					{game.trending && (
						<span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-accent/90 text-white flex items-center gap-1">
							<svg
								viewBox="0 0 16 16"
								fill="currentColor"
								className="h-2.5 w-2.5"
								aria-hidden="true"
							>
								<path d="M8 1C5.5 4 4 6 4 8.5a4 4 0 008 0C12 6 10.5 4 8 1z" />
							</svg>
							Hot
						</span>
					)}
					{game.featured && !game.trending && (
						<span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gold text-background">
							Featured
						</span>
					)}
				</div>
			</div>

			{/* Meta row */}
			<div className={isCompact ? "p-3" : "p-4"}>
				<h3
					className={`font-display font-bold text-ink truncate ${isCompact ? "text-sm" : "text-base"}`}
				>
					{game.title}
				</h3>
				<p className="text-xs text-ink-faint truncate mt-0.5">
					by {game.creator}
				</p>
				{!isCompact && (
					<div className="mt-2.5 flex items-center gap-3">
						<div className="flex items-center gap-1.5">
							<RatingStars rating={game.rating} />
							<span className="text-xs font-semibold text-ink-muted">
								{game.rating.toFixed(1)}
							</span>
						</div>
						<span className="text-xs text-ink-faint flex items-center gap-1">
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-3 w-3"
								aria-hidden="true"
							>
								<path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-2a3 3 0 100-6 3 3 0 000 6z" />
							</svg>
							{formatPlays(game.plays)}
						</span>
					</div>
				)}
			</div>
		</Link>
	);
}
